namespace $.$$ {

	const $shm_hitalama_map_widget_any_default = Symbol( '$shm_hitalama_map_widget_any_default' )

	export type $shm_hitalama_map_widget_any_widget_type = keyof ReturnType< $.$shm_hitalama_map_widget_any['widgets'] >
	export type $shm_hitalama_map_widget_any_widget = ReturnType< $.$shm_hitalama_map_widget_any['widgets'] >[ $shm_hitalama_map_widget_any_widget_type ]
	export type $shm_hitalama_map_widget_any_widgets = Partial< Record< $shm_hitalama_map_widget_def_type_name, $shm_hitalama_map_widget_any_widget > >

	export class $shm_hitalama_map_widget_any extends $.$shm_hitalama_map_widget_any {

		@ $mol_mem
		custom_expose() {
			this.$.$shm_hitalama_compat_fqn_install( this.$ as any )
			const custom = this.widget().Type_custom()?.remote()!

			const class_name = custom.class_name()!
			const code_css = this.$.$shm_hitalama_compat_fqn_css( custom.Code_css()?.value()! )
			this.$.$mol_style_attach( class_name, code_css )

			const code_tree = custom.Code_view_tree()?.value()!
			const code_js = custom.Code_js()?.value()!
			
			const tree = this.$.$mol_view_tree2_normalize(
				this.$.$mol_tree2_from_string( code_tree )
			).kids[0]

			const base_js = this.$.$mol_tree2_text_to_string_mapped_js(
				this.$.$mol_tree2_js_to_text(
					this.$.$mol_view_tree2_to_js(
						tree.list([ tree ])
					)
				)
			)
			
			const code = `const $ = this.$;\n`+
				`$.${ class_name } = ${ base_js };\n`+
				`${code_js};`
			
			const func = new Function( code )
			func.call( { $: this.$ } )
		}
		
		@ $mol_mem
		Custom(){
			if( this.widget().type() !== 'custom' ) return super.Sub()
				
			const custom = this.widget().Type_custom()?.remote()!
			const code_css = custom.Code_css()?.value()!
			const code_tree = custom.Code_view_tree()?.value()!
			const code_js = custom.Code_js()?.value()!

			const class_name = custom?.class_name()!

			try {
				this.custom_expose()
				const obj = new (this.$ as any)[ class_name ]
				return obj as $shm_hitalama_map_widget_float
				
			} catch (error) {
				if( !$mol_promise_like(error) ) {
					console.error( error )
					return super.Sub()
				}
				else throw error
			}
		}

		@ $mol_mem
		Sub(): $shm_hitalama_map_widget_float {
			const type = this.widget().type()
			if( type != 'text' ) {
				const code_view = this.code_widget_view()
				if( code_view ) return code_view
			}

			return this.widget_view( type )
		}

		@ $mol_mem_key
		Sub_hack( type_hack: $shm_hitalama_map_widget_def_type_name ) {
			return this.widget_view( type_hack )
		}

		widget_view( type: $shm_hitalama_map_widget_def_type_name ): $shm_hitalama_map_widget_float {
			const base = ( this.widgets() as $shm_hitalama_map_widget_any_widgets )[ type ]
			if( !base ) throw new Error( `Widget view is not defined for type: ${ type }` )

			this.setup_obj( base )
			return base as $shm_hitalama_map_widget_float
		}

		code_widget_result(): unknown {
			const widget = this.widget()

			const code_widget = widget.code_widget()
			if( !code_widget?.enabled() ) return $shm_hitalama_map_widget_any_default
			if( code_widget?.type() == 'code_html' ) return $shm_hitalama_map_widget_any_default
			if( code_widget.view_widget()?.link_str() !== widget.link_str() ) return $shm_hitalama_map_widget_any_default

			const code = code_widget.code()
			const runtime = this.Map_page().map().runtime()
			const context = {
				[ '$' + 'page' ]: this.Map_page(),
				[ '$' + 'this_widget' ]: code_widget,
				[ '$' + 'make' ]: this.code_widget_make.bind( this ),
			}

			const deps = runtime.extract_reactive_deps( code )
			const result = this.$.$mol_error_fence(
				()=> {
					if( deps ) runtime.execute( deps, context )
					return code.includes( 'await' )
						? runtime.execute_action( code, context )
						: runtime.execute( code, context )
				},
				error => {
					return this.code_widget_error_view( error )
				},
			)

			if( this.$.$mol_promise_like( result ) ) throw result
			return result
		}

		code_widget_make<View extends object>(
			View: ( new()=> View ) | $shm_hitalama_map_widget_def_type_name,
			config: Record< string, any > = {},
		): View | $shm_hitalama_map_widget_float {
			if( typeof View !== 'string' ) {
				return this.$.$shm_hitalama_map_runtime_make( View, config )
			}

			const base = ( this.widgets() as $shm_hitalama_map_widget_any_widgets )[ View ]
			if( !base ) throw new Error( `Widget view is not defined for type: ${ View }` )

			const obj = new ( base.constructor as new()=> $.$shm_hitalama_map_widget_float )
			this.setup_obj( obj )
			this.setup_config( obj, config )
			return obj as $shm_hitalama_map_widget_float
		}

		code_widget_error_view( error: unknown ) {
			return this.code_widget_make( this.widget().type(), {
				controls: ()=> {
					const view = new this.$.$mol_view()
					view.sub = ()=> { throw error }
					return [ view ]
				},
			} )
		}

		setup_config( obj: object, config: Record< string, any > ) {
			for( const key in config ) {
				const value = config[ key ]
				;( obj as any )[ key ] = typeof value === 'function'
					? value
					: ()=> value
			}
		}

		code_widget_view(): $shm_hitalama_map_widget_float | null {
			const result = this.code_widget_result()
			if( result === $shm_hitalama_map_widget_any_default ) return null
			if( result === null ) return null
			if( !( result instanceof this.$.$shm_hitalama_map_widget_float ) ) return null

			this.setup_obj( result )
			return result
		}

		code_widget_hidden(): boolean {
			return this.code_widget_result() === null
		}

		setup_obj( obj: $.$shm_hitalama_map_widget_float ) {
			obj.widget = () => this.widget()
			obj.Map_page = () => this.Map_page()
		}
		
	}
	
}
